import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Plus, X, Trash2, Save, Database, Upload, FileText, Download,
  AlertTriangle, RefreshCw, FileSignature, Calculator, Loader2,
} from 'lucide-react';
import DocumentTemplateTab from './DocumentTemplateTab';
import { toast } from 'sonner';

const API = API_BASE;

// 04.02.2026 (User-Wunsch): Komponenten pro System werden alphabetisch
// nach Hersteller sortiert. Als Modul-Helper definiert, damit sie auch
// beim Laden (nicht nur beim Editieren) angewendet werden kann.
function sortComponentsByHersteller(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  arr.sort((a, b) => {
    const ha = (a?.hersteller || '').trim();
    const hb = (b?.hersteller || '').trim();
    if (!ha && !hb) return 0;
    if (!ha) return 1;   // leere Hersteller ans Ende
    if (!hb) return -1;
    const cmp = ha.localeCompare(hb, 'de', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
    return (a?.modell || '').localeCompare(b?.modell || '', 'de', { sensitivity: 'base' });
  });
  return arr;
}

export default function SystemDatabasePage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [systems, setSystems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [mainTab, setMainTab] = useState('systems');   // systems | template
  // Granular: Action-Matrix entscheidet — siehe /api/permissions/me.
  const canEdit = can('systems.update');
  const canCreate = can('systems.create');
  const canDelete = can('systems.delete');
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  // Herstellungs-Kalkulation als PDF (28.02.2026, Admin-only).
  // Fixed-Bar am Ende des Systems-Panels: Stundenlohn eingeben → PDF-Download.
  const [hourlyRate, setHourlyRate] = useState(65);
  const [downloadingCalc, setDownloadingCalc] = useState(false);
  const downloadCostPdf = async (system) => {
    if (!system?.id) return;
    setDownloadingCalc(true);
    try {
      const r = await authFetch(`/api/systems/${system.id}/cost-calculation-pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hourlyRateEur: Number(hourlyRate) || 0 }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Kalkulation konnte nicht erzeugt werden');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kalkulation_${(system.name || 'System').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success('Kalkulation heruntergeladen');
    } finally {
      setDownloadingCalc(false);
    }
  };

  const load = useCallback(async (keepActive = true) => {
    setLoading(true);
    const r = await authFetch('/api/systems');
    if (r.ok) {
      const d = await r.json();
      // 04.02.2026: beim Laden bereits Komponenten pro System alphabetisch
      // nach Hersteller ordnen — dann ist der erste Eindruck sofort korrekt.
      const sortedSystems = (d.systems || []).map(s => ({
        ...s, components: sortComponentsByHersteller(s.components || []),
      }));
      setSystems(sortedSystems);
      if (!keepActive || !activeId) {
        if (sortedSystems.length) setActiveId(sortedSystems[0].id);
      } else if (!sortedSystems.find(s => s.id === activeId)) {
        setActiveId(sortedSystems[0]?.id || null);
      }
    }
    setLoading(false);
  }, [activeId]);

  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const active = useMemo(() => systems.find(s => s.id === activeId), [systems, activeId]);

  // 04.02.2026: Autocomplete-Vorschläge für das Hersteller-Feld.
  // Sammelt alle bereits verwendeten Hersteller ÜBER ALLE Systeme
  // (case-insensitive dedupliziert, alphabetisch). Wird über ein
  // <datalist> an den Input gebunden — Browser blenden Vorschläge
  // automatisch beim Tippen ein, ohne dass wir ein eigenes Dropdown
  // rendern müssen. Bonus: Der User kann Tippfehler vermeiden, wodurch
  // die alphabetische Sortierung stabil bleibt (nicht ein neues Cluster
  // pro Schreibvariante).
  const knownHerstellerList = useMemo(() => {
    const map = new Map();  // lower-case key → Original-Schreibweise (erste Sichtung)
    for (const sys of systems) {
      for (const c of (sys?.components || [])) {
        const h = (c?.hersteller || '').trim();
        if (!h) continue;
        const key = h.toLowerCase();
        if (!map.has(key)) map.set(key, h);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }),
    );
  }, [systems]);

  const patchActive = (patch) => {
    setSystems(s => s.map(x => x.id === activeId ? { ...x, ...patch } : x));
    setDirty(true);
  };

  // 04.02.2026: Alias auf den Modul-Helper — Sortierung wird beim
  // Hinzufügen, on-blur des Hersteller-Feldes und beim Speichern
  // angewendet. Beim Editieren selbst sortieren wir NICHT (sonst würde
  // die aktive Zeile beim Tippen springen).
  const sortComponents = sortComponentsByHersteller;

  const addComponent = () => {
    patchActive({
      components: sortComponents([
        ...(active?.components || []),
        { id: crypto.randomUUID(), hersteller: '', modell: '', anzahl: 1 },
      ]),
    });
  };
  const delComponent = (cid) => {
    patchActive({ components: (active?.components || []).filter(c => c.id !== cid) });
  };
  const editComponent = (cid, patch) => {
    patchActive({
      components: (active?.components || []).map(c => c.id === cid ? { ...c, ...patch } : c),
    });
  };
  const sortComponentsNow = () => {
    if (!active) return;
    patchActive({ components: sortComponents(active.components || []) });
  };

  const save = async () => {
    if (!active) return;
    setSaving(true); setMsg('');
    // 04.02.2026: Vor dem Speichern nach Hersteller sortieren, damit die
    // kanonische DB-Reihenfolge alphabetisch ist.
    const sortedComponents = sortComponents(active.components || []);
    const r = await authFetch(`/api/systems/${active.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: active.name,
        components: sortedComponents.map(c => ({
          hersteller: c.hersteller || '', modell: c.modell || '',
          anzahl: Number(c.anzahl) || 0,
        })),
        assemblyTimeMin: active.assemblyTimeMin != null && active.assemblyTimeMin !== '' ? Number(active.assemblyTimeMin) : null,
        configurationTimeMin: active.configurationTimeMin != null && active.configurationTimeMin !== '' ? Number(active.configurationTimeMin) : null,
        packagingShippingTimeMin: active.packagingShippingTimeMin != null && active.packagingShippingTimeMin !== '' ? Number(active.packagingShippingTimeMin) : null,
      }),
    });
    if (r.ok) {
      const updated = await r.json();
      setSystems(s => s.map(x => x.id === active.id ? updated : x));
      setDirty(false);
      setMsg('✓ Gespeichert');
      setTimeout(() => setMsg(''), 2000);
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg(d.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  const createSystem = async () => {
    if (!newName.trim()) return;
    const r = await authFetch('/api/systems', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim(), components: [] }),
    });
    if (r.ok) {
      const created = await r.json();
      setSystems(s => [...s, created]);
      setActiveId(created.id);
      setShowNew(false);
      setNewName('');
    } else {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || 'Fehler');
    }
  };

  const removeSystem = async () => {
    if (!active) return;
    if (!window.confirm(`System "${active.name}" wirklich löschen?`)) return;
    const r = await authFetch(`/api/systems/${active.id}`, { method: 'DELETE' });
    if (r.ok) {
      await load(false);
    } else {
      alert('Löschen fehlgeschlagen');
    }
  };

  const uploadSchematic = async (file) => {
    if (!active || !file) return;
    if (file.type !== 'application/pdf') { alert('Nur PDF-Dateien erlaubt'); return; }
    const fd = new FormData();
    fd.append('file', file);
    const r = await authFetch(`/api/systems/${active.id}/schematic`, { method: 'POST', body: fd });
    if (r.ok) { await load(); }
    else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Upload fehlgeschlagen'); }
  };

  const deleteSchematic = async () => {
    if (!active) return;
    if (!window.confirm('Schaltplan wirklich löschen?')) return;
    const r = await authFetch(`/api/systems/${active.id}/schematic`, { method: 'DELETE' });
    if (r.ok) await load();
  };

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2" data-testid="systems-title">
            <Database className="w-5 h-5 text-[#9ec043]" /> Systemdatenbank
          </h1>
          <p className="text-xs text-gray-400">Produktsysteme (BOM) — Stückliste, Schaltplan, Grundlage für Lagerabzug</p>
        </div>
        <div className="flex items-center gap-2">
          {mainTab === 'systems' && (
            <>
              <Button variant="outline" onClick={() => load()} className="text-sm" data-testid="btn-reload-systems">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Neu laden
              </Button>
              {canCreate && (
                <Button onClick={() => setShowNew(true)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-system">
                  <Plus className="w-4 h-4 mr-1" /> System hinzufügen
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 mb-4 flex gap-2" data-testid="sysdb-main-tabs">
        <button onClick={() => setMainTab('systems')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mainTab === 'systems' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          data-testid="tab-systems">
          <Database className="w-4 h-4 inline mr-1.5" /> Systeme ({systems.length})
        </button>
        <button onClick={() => setMainTab('template')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mainTab === 'template' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          data-testid="tab-template">
          <FileSignature className="w-4 h-4 inline mr-1.5" /> Begleitdokument-Vorlage
        </button>
      </div>

      {mainTab === 'template' && <DocumentTemplateTab />}

      {mainTab === 'systems' && (
        <>
          {/* System tabs */}
          <div className="bg-white border border-gray-200 rounded-xl p-2 mb-4 flex flex-wrap gap-1" data-testid="system-tabs">
        {loading && <span className="text-xs text-gray-400 px-2 py-1.5">Lade…</span>}
        {systems.map(s => (
          <button key={s.id} onClick={() => { if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return; setActiveId(s.id); setDirty(false); }}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${activeId === s.id ? 'bg-[#9ec043] border-[#9ec043] text-white font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            data-testid={`tab-system-${s.name.replace(/\s/g, '-')}`}>
            {s.name}
          </button>
        ))}
      </div>

      {showNew && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4" data-testid="new-system-form">
          <h3 className="text-sm font-semibold text-[#32373c] mb-2">Neues System</h3>
          <div className="flex gap-2">
            <Input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="z. B. HS 20 PV" className="text-sm" data-testid="new-system-name"
              onKeyDown={e => e.key === 'Enter' && createSystem()} />
            <Button onClick={createSystem} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-create-system">Anlegen</Button>
            <Button variant="outline" onClick={() => { setShowNew(false); setNewName(''); }} className="text-sm">Abbrechen</Button>
          </div>
        </div>
      )}

      {active && (
        <div className="space-y-4" data-testid="system-detail">
          {/* Header with name edit & actions */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-gray-400">Systemname</label>
                <Input value={active.name} onChange={e => patchActive({ name: e.target.value })}
                  className="mt-0.5 text-base font-semibold" disabled={!canEdit} data-testid="system-name" />
              </div>
              {canEdit && (
                <div className="flex gap-2 shrink-0">
                  <Button onClick={save} disabled={!dirty || saving}
                    className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm disabled:opacity-50" data-testid="btn-save-system">
                    <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
                  </Button>
                  {user?.role === 'admin' && (
                    <Button variant="outline" onClick={removeSystem} className="text-sm text-red-500 hover:bg-red-50" data-testid="btn-delete-system">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                    </Button>
                  )}
                </div>
              )}
            </div>
            {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`} data-testid="system-save-msg">{msg}</div>}
          </div>

          {/* Components */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-sm font-semibold text-[#32373c]">Komponenten ({active.components?.length || 0})</h2>
              {canEdit && (
                <Button onClick={addComponent} variant="outline" className="text-sm" data-testid="btn-add-component">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Zeile hinzufügen
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="text-left px-4 py-2 w-1/3">Hersteller</th>
                    <th className="text-left px-4 py-2">Modell</th>
                    <th className="text-right px-4 py-2 w-24">Anzahl</th>
                    {canEdit && <th className="px-4 py-2 w-12" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-testid="component-table">
                  {/* 04.02.2026: Autocomplete-Quelle für alle Hersteller-Inputs.
                      Einmal pro System sichtbar — Browser rendert Dropdown selbst. */}
                  <datalist id="known-hersteller-list">
                    {knownHerstellerList.map(h => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                  {(active.components || []).map(c => (
                    <tr key={c.id} data-testid={`component-row-${c.id}`}>
                      <td className="px-4 py-2">
                        <Input value={c.hersteller || ''} onChange={e => editComponent(c.id, { hersteller: e.target.value })}
                          onBlur={sortComponentsNow}
                          list="known-hersteller-list"
                          autoComplete="off"
                          className="text-sm h-8 border-gray-200" placeholder="z. B. Victron Energy" disabled={!canEdit} />
                      </td>
                      <td className="px-4 py-2">
                        <Input value={c.modell || ''} onChange={e => editComponent(c.id, { modell: e.target.value })}
                          className="text-sm h-8 border-gray-200" placeholder="z. B. Multiplus II 5000" disabled={!canEdit} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Input type="number" step="0.5" min="0" value={c.anzahl ?? 0}
                          onChange={e => editComponent(c.id, { anzahl: e.target.value })}
                          className="text-sm h-8 text-right tabular-nums border-gray-200 w-20 ml-auto" disabled={!canEdit} />
                      </td>
                      {canEdit && (
                        <td className="px-2 text-right">
                          <button onClick={() => delComponent(c.id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 rounded" data-testid={`btn-del-component-${c.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {(active.components || []).length === 0 && (
                    <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-6 text-center text-xs text-gray-400">
                      Noch keine Komponenten — „Zeile hinzufügen" klicken.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Produktion & Lager — Arbeitszeit pro System */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-amber-50/40">
              <h2 className="text-sm font-semibold text-[#32373c]">Produktion & Lager (Arbeitszeit)</h2>
              <p className="text-[10px] text-gray-500 mt-1">
                Effektive Arbeitszeit in <strong>Minuten pro System</strong> — wird vom Produktions- bzw.
                Lager-Team gepflegt. Fließt in Kostenkalkulation und Lieferterminplanung ein.
              </p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-3">
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Konfektion / Aufbau (min)
                  <Input type="number" min="0" step="1" value={active.assemblyTimeMin ?? ''}
                         onChange={e => patchActive({ assemblyTimeMin: e.target.value })}
                         className="mt-1 text-sm h-9 border-gray-200" disabled={!canEdit}
                         data-testid="sys-assembly-min" />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Konfiguration (min)
                  <Input type="number" min="0" step="1" value={active.configurationTimeMin ?? ''}
                         onChange={e => patchActive({ configurationTimeMin: e.target.value })}
                         className="mt-1 text-sm h-9 border-gray-200" disabled={!canEdit}
                         data-testid="sys-config-min" />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-gray-500">Verpackung & Versand (min)
                  <Input type="number" min="0" step="1" value={active.packagingShippingTimeMin ?? ''}
                         onChange={e => patchActive({ packagingShippingTimeMin: e.target.value })}
                         className="mt-1 text-sm h-9 border-gray-200" disabled={!canEdit}
                         data-testid="sys-packaging-min" />
                </label>
              </div>
              {((Number(active.assemblyTimeMin) || 0)
                + (Number(active.configurationTimeMin) || 0)
                + (Number(active.packagingShippingTimeMin) || 0)) > 0 && (
                <div className="mt-3 text-[11px] text-amber-800" data-testid="sys-total-worktime">
                  Gesamt-Arbeitszeit:&nbsp;
                  <strong>
                    {(Number(active.assemblyTimeMin) || 0) +
                     (Number(active.configurationTimeMin) || 0) +
                     (Number(active.packagingShippingTimeMin) || 0)} min
                  </strong>
                  &nbsp;(={(((Number(active.assemblyTimeMin) || 0)
                          + (Number(active.configurationTimeMin) || 0)
                          + (Number(active.packagingShippingTimeMin) || 0)) / 60).toFixed(2)} h)
                </div>
              )}
            </div>
          </div>

          {/* Schaltplan */}
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="schematic-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#9ec043]" /> Schaltplan
              </h2>
            </div>
            {active.schematic ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <FileText className="w-4 h-4 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#32373c] font-medium truncate">{active.schematic.filename}</div>
                  <div className="text-[10px] text-gray-400">
                    {(active.schematic.size / 1024 / 1024).toFixed(2)} MB · hochgeladen {new Date(active.schematic.uploadedAt).toLocaleString('de-DE')}
                  </div>
                </div>
                <a href={`${API}/api/systems/${active.id}/schematic`} target="_blank" rel="noreferrer"
                  className="p-2 text-gray-400 hover:text-[#9ec043]" data-testid="btn-view-schematic">
                  <Download className="w-4 h-4" />
                </a>
                {canEdit && (
                  <button onClick={deleteSchematic} className="p-2 text-gray-400 hover:text-red-500" data-testid="btn-del-schematic">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400">Noch kein Schaltplan hinterlegt.</div>
            )}
            {canEdit && (
              <div className="mt-3">
                <label className="inline-flex items-center gap-2 text-xs text-[#9ec043] hover:text-[#8aad38] cursor-pointer" data-testid="btn-upload-schematic">
                  <Upload className="w-3.5 h-3.5" /> {active.schematic ? 'Ersetzen (PDF)' : 'PDF hochladen'}
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={e => { uploadSchematic(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </div>
            )}
            {active.schematic && (
              <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden" style={{ height: '60vh' }}>
                <iframe title="Schaltplan" src={`${API}/api/systems/${active.id}/schematic`} className="w-full h-full"
                  data-testid="schematic-iframe" />
              </div>
            )}
          </div>

          {/* Admin-only: Herstellungs-Kalkulation als PDF (28.02.2026).
              Materialkosten = Σ (Komponenten × EK-Preis aus Lager),
              Arbeitskosten = (Konfektion + Konfig + Verpackung) × Stundenlohn. */}
          {isAdmin && (
            <div className="bg-white border border-gray-200 rounded-xl p-4"
                 data-testid="cost-calculation-panel">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-[#9ec043]" />
                <h2 className="text-sm font-semibold text-[#32373c]">Herstellungs-Kalkulation (Admin)</h2>
              </div>
              <p className="text-[10px] text-gray-500 mb-3">
                Nur für Administratoren. Erzeugt eine PDF mit Materialkosten
                (Komponenten × <strong>Lager-EK-Preise</strong>) und
                Arbeitskosten (<strong>Gesamt-Arbeitszeit × Stundenlohn</strong>).
                Preise sind netto und vertraulich.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-[10px] uppercase tracking-wide text-gray-500 flex-1 min-w-[140px] max-w-[200px]">
                  Stundenlohn (€ / h)
                  <Input
                    type="number" min="0" step="0.5" value={hourlyRate}
                    onChange={e => setHourlyRate(e.target.value)}
                    className="mt-1 text-sm h-9 border-gray-200"
                    data-testid="cost-hourly-rate"
                  />
                </label>
                <Button
                  onClick={() => downloadCostPdf(active)}
                  disabled={downloadingCalc || !(Number(hourlyRate) >= 0)}
                  className="h-9 bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm"
                  data-testid="btn-download-cost-pdf"
                >
                  {downloadingCalc
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> PDF wird erzeugt…</>
                    : <><Download className="w-3.5 h-3.5 mr-1" /> Kalkulation als PDF</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!active && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Noch kein System angelegt.
        </div>
      )}
      </>
      )}
    </div>
  );
}
